import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Film, Loader2, Play, Download, RefreshCw, Copy, Square, Trash2, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ORANGE = "#FF8A22";
const ACTIVE = ["queued", "preparing", "generating", "rendering", "uploading"];
const STATUS_COLORS = {
  queued: "#9DA3B4", preparing: "#FFD23F", generating: "#FF8A22", rendering: "#FF8A22",
  uploading: "#3AB4FF", completed: "#00DC82", failed: "#F87171", cancelled: "#6F7585",
};

function Chip({ s }) {
  return <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ color: STATUS_COLORS[s], borderColor: `${STATUS_COLORS[s]}55` }}>{s}</span>;
}

export default function VideoGenerator() {
  const { refreshUser } = useAuth();
  const [caps, setCaps] = useState(null);
  const [denied, setDenied] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [duration, setDuration] = useState(4);
  const [ratio, setRatio] = useState("9:16");
  const [quality, setQuality] = useState("standard");
  const [style, setStyle] = useState("cinematic");
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = () => api.get("/video/jobs").then((r) => setJobs(r.data.items)).catch(() => {});

  useEffect(() => {
    api.get("/video/capabilities").then((r) => {
      if (!r.data.access) { setDenied(r.data.reason || "restricted"); return; }
      setCaps(r.data);
      if (r.data.defaults) { setRatio(r.data.defaults.ratio); setQuality(r.data.defaults.quality); }
      loadJobs();
    }).catch(() => setDenied("restricted"));
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (jobs.some((j) => ACTIVE.includes(j.status))) {
      pollRef.current = setInterval(() => { loadJobs(); refreshUser(); }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [jobs.map((j) => j.status).join(",")]);

  const qualityInfo = (caps?.qualities || []).find((x) => x.key === quality);
  const base = Number(caps?.pricing?.[String(duration)] ?? 0);
  const cost = caps ? base + (qualityInfo?.extra_cost || 0) : "—";
  const dims = qualityInfo?.dims?.[ratio];

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Enter a prompt");
    setBusy(true);
    try {
      await api.post("/video/jobs", { prompt, negative_prompt: negative || null, duration, aspect_ratio: ratio, quality, style });
      toast.success("Video queued — you can leave this page, it runs in the background");
      setPrompt(""); refreshUser(); loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not queue video"); }
    finally { setBusy(false); }
  };

  const act = async (job_id, action) => {
    try { await api.post(`/video/jobs/${job_id}/${action}`); refreshUser(); loadJobs(); toast.success(action === "cancel" ? "Cancelled" : "New job queued"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const remove = async (job_id) => { await api.delete(`/video/jobs/${job_id}`).catch(() => {}); loadJobs(); };

  if (denied) {
    return (
      <div className="p-8 max-w-xl mx-auto pt-20 text-center kr-card p-10 mt-10" data-testid="video-access-denied">
        <ShieldAlert className="h-10 w-10 mx-auto" style={{ color: ORANGE }} />
        <h1 className="font-display text-2xl mt-5">Video Generator is restricted</h1>
        <p className="text-sm text-white/50 mt-3">
          {denied === "maintenance" ? "The Video Generator is under maintenance. Check back soon."
            : denied === "expired" ? "Your Video Generator access has expired. Contact your administrator to renew it."
            : "This module is available to selected users only. Contact your administrator to request access."}
        </p>
      </div>
    );
  }
  if (!caps) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto kr-page">
      <div>
        <div className="kr-overline" style={{ color: ORANGE }}>[ VIDEO GENERATOR ]</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Video Generator</h1>
        <p className="text-[#9DA3B4] text-sm mt-1.5">Generate AI videos with {caps.capabilities.name}. Jobs run in the background — leave anytime.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Controls */}
        <div className="lg:col-span-4 kr-card p-4 sm:p-6 space-y-5 h-fit">
          <div>
            <div className="kr-overline mb-2">[ PROMPT ]</div>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
              placeholder="A drone shot over a futuristic city at golden hour, cinematic lighting…"
              className="bg-[#07080B] border-white/10 resize-none text-base" data-testid="video-prompt-input" />
          </div>
          <div>
            <div className="kr-overline mb-2">NEGATIVE PROMPT (OPTIONAL)</div>
            <Input value={negative} onChange={(e) => setNegative(e.target.value)} placeholder="blurry, text, watermark"
              className="bg-[#07080B] border-white/10 h-11" data-testid="video-negative-input" />
          </div>
          <div>
            <div className="kr-overline mb-2">DURATION</div>
            <div className="flex gap-2" data-testid="video-duration-picker">
              {caps.capabilities.durations.map((d) => (
                <button key={d} onClick={() => setDuration(d)}
                  className={`flex-1 h-11 rounded-lg border font-mono text-sm ${duration === d ? "text-black font-bold" : "text-white/60 hover:text-white"}`}
                  style={{ borderColor: duration === d ? ORANGE : "rgba(255,255,255,0.08)", background: duration === d ? ORANGE : "transparent" }}
                  data-testid={`video-duration-${d}`}>
                  {d}s · {caps.pricing[String(d)]} cr
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="kr-overline mb-2">ASPECT RATIO</div>
            <div className="grid grid-cols-4 gap-2" data-testid="video-ratio-picker">
              {(caps.ratios || []).map((r) => {
                const [w, h] = r.key.split(":").map(Number);
                const bw = 16 * Math.min(1, w / h), bh = 16 * Math.min(1, h / w);
                return (
                  <button key={r.key} onClick={() => setRatio(r.key)}
                    className={`p-2 rounded-lg border flex flex-col items-center gap-1.5 min-h-[64px] ${ratio === r.key ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}
                    style={{ borderColor: ratio === r.key ? ORANGE : "rgba(255,255,255,0.08)" }}
                    data-testid={`video-ratio-${r.key.replace(":", "x")}`}>
                    <span className="rounded-[2px] border" style={{ width: bw + 4, height: bh + 4, borderColor: ratio === r.key ? ORANGE : "rgba(255,255,255,0.35)" }} />
                    <span className={`text-[10px] font-mono ${ratio === r.key ? "text-white" : "text-white/45"}`}>{r.key}</span>
                  </button>
                );
              })}
            </div>
            {ratio === "9:16" && <div className="text-[11px] mt-1.5" style={{ color: ORANGE }}>Recommended for Reels · TikTok · Shorts · Stories</div>}
            {(ratio === "1:1" || ratio === "4:5") && <div className="text-[11px] text-white/35 mt-1.5">Generated natively in portrait, then precision center-cropped to {ratio}.</div>}
          </div>
          <div>
            <div className="kr-overline mb-2">QUALITY</div>
            <div className="grid grid-cols-3 gap-2" data-testid="video-quality-picker">
              {(caps.qualities || []).map((qi) => (
                <button key={qi.key} onClick={() => setQuality(qi.key)}
                  className={`p-2.5 rounded-lg border text-center min-h-[64px] ${quality === qi.key ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}
                  style={{ borderColor: quality === qi.key ? ORANGE : "rgba(255,255,255,0.08)" }}
                  data-testid={`video-quality-${qi.key}`}>
                  <div className={`text-xs font-semibold ${quality === qi.key ? "text-white" : "text-white/60"}`}>{qi.label}</div>
                  <div className="text-[10px] font-mono text-white/40 mt-1">{qi.dims?.[ratio]?.join("×")}</div>
                  <div className="text-[10px] font-mono mt-0.5" style={{ color: qi.extra_cost ? ORANGE : "rgba(255,255,255,0.35)" }}>
                    {qi.extra_cost ? `+${qi.extra_cost} cr` : "base"}
                  </div>
                </button>
              ))}
            </div>
            {quality === "best" && <div className="text-[11px] text-white/35 mt-1.5">Highest native output + real AI upscaling — labeled as upscaled in metadata.</div>}
          </div>
          <div>
            <div className="kr-overline mb-2">STYLE</div>
            <div className="ac-hscroll gap-2 pb-1">
              {caps.capabilities.styles.map((s) => (
                <button key={s} onClick={() => setStyle(s)}
                  className={`shrink-0 px-3.5 py-2 rounded-full border text-xs capitalize min-h-[36px] ${style === s ? "text-black font-semibold" : "text-white/60 hover:text-white"}`}
                  style={{ borderColor: style === s ? ORANGE : "rgba(255,255,255,0.1)", background: style === s ? ORANGE : "transparent" }}
                  data-testid={`video-style-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="sticky bottom-[calc(env(safe-area-inset-bottom)+80px)] lg:static z-10 -mx-2 px-2 py-2 rounded-xl bg-[#101219]/95 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-0 lg:p-0 lg:m-0">
            <Button onClick={generate} disabled={busy} className="w-full rounded-xl h-12 font-semibold text-black border-0" style={{ background: ORANGE }} data-testid="video-generate-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Generate · {cost} cr</>}
            </Button>
            <div className="text-center text-[11px] text-white/35 mt-1.5">
              {dims ? `${ratio} · ${dims.join("×")} · ` : ""}Estimated {cost} credits — refunded automatically if generation fails
            </div>
          </div>
        </div>

        {/* Jobs */}
        <div className="lg:col-span-8 space-y-3">
          {jobs.length === 0 && (
            <div className="kr-card p-10 text-center text-white/40">
              <Film className="h-9 w-9 mx-auto text-white/20" />
              <div className="text-sm mt-3">No video jobs yet — your generations will appear here with live status.</div>
            </div>
          )}
          {jobs.map((j) => (
            <div key={j.job_id} className="kr-card p-4 sm:p-5" data-testid={`video-job-${j.job_id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm text-white/90 line-clamp-2">{j.prompt}</div>
                  <div className="text-[11px] text-white/35 mt-1">
                    {j.duration}s · {j.aspect_ratio || "16:9"} · {j.resolution}{j.quality && j.quality !== "standard" ? ` · ${j.quality.toUpperCase()}` : ""} · {j.credits_reserved} cr · {new Date(j.created_at).toLocaleString()}
                  </div>
                </div>
                <Chip s={j.status} />
              </div>
              {ACTIVE.includes(j.status) && (
                <div className="mt-3">
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(j.progress, 5)}%`, background: ORANGE }} />
                  </div>
                </div>
              )}
              {j.error && <div className="mt-3 text-xs text-red-300 border border-red-400/25 rounded-lg p-2.5" data-testid="video-job-error">{j.error} — credits refunded.</div>}
              {j.status === "completed" && j.result?.url && (
                <>
                  <video controls preload="metadata" className="mt-3 w-full max-h-[480px] object-contain rounded-lg border border-white/10 bg-black" data-testid="video-job-player">
                    <source src={`${BACKEND_URL}${j.result.url}`} type="video/mp4" />
                  </video>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="video-job-meta">
                    {[`${j.result.width || "?"}×${j.result.height || "?"}`,
                      j.result.source || "Native",
                      j.result.fps ? `${j.result.fps} fps` : null,
                      j.result.file_size ? `${(j.result.file_size / 1048576).toFixed(1)} MB` : null,
                      j.result.model || null].filter(Boolean).map((m, i) => (
                      <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-white/10 text-white/45">{m}</span>
                    ))}
                  </div>
                </>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                {j.status === "queued" && (
                  <Button variant="outline" onClick={() => act(j.job_id, "cancel")} className="h-8 text-xs border-white/10 hover:bg-white/5 hover:text-white" data-testid="video-job-cancel"><Square className="h-3 w-3 mr-1.5" /> Cancel</Button>
                )}
                {j.status === "failed" && (
                  <Button variant="outline" onClick={() => act(j.job_id, "retry")} className="h-8 text-xs border-white/10 hover:bg-white/5 hover:text-white" data-testid="video-job-retry"><RefreshCw className="h-3 w-3 mr-1.5" /> Retry</Button>
                )}
                {j.status === "completed" && (
                  <>
                    <a href={`${BACKEND_URL}${j.result.url}`} download target="_blank" rel="noreferrer">
                      <Button variant="outline" className="h-8 text-xs border-white/10 hover:bg-white/5 hover:text-white" data-testid="video-job-download"><Download className="h-3 w-3 mr-1.5" /> Download</Button>
                    </a>
                    <Button variant="outline" onClick={() => act(j.job_id, "variation")} className="h-8 text-xs border-white/10 hover:bg-white/5 hover:text-white" data-testid="video-job-variation"><Play className="h-3 w-3 mr-1.5" /> Variation</Button>
                  </>
                )}
                {["completed", "failed", "cancelled"].includes(j.status) && (
                  <>
                    <Button variant="outline" onClick={() => act(j.job_id, "duplicate")} className="h-8 text-xs border-white/10 hover:bg-white/5 hover:text-white" data-testid="video-job-duplicate"><Copy className="h-3 w-3 mr-1.5" /> Duplicate</Button>
                    <button onClick={() => remove(j.job_id)} className="ml-auto text-white/30 hover:text-red-300 p-1.5" aria-label="Delete job" data-testid="video-job-delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
